import React, { useState, useRef } from 'react';
import Select from 'react-select';
import ModalBasic from './ModalBasic';
import { baseAPI } from '../config';
import axios from 'axios';
import { toast } from 'react-toastify';
import { COUNTRY_PREFIXES, DEFAULT_COUNTRY_PREFIX } from '@claimup/shared/constants/country-prefixes';
import { ChevronUpIcon, ChevronDownIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

function UpdateDebtor({ idDebtor }) {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [debtorData, setDebtorData] = useState({
    debtorName: '',
    debtorAddress: '',
    debtorCity: '',
    debtorZip: '',
    debtorEmail: '',
    debtornif: '',
  });
  // Lista de teléfonos: el PRIMERO es el por defecto para envío. `note` es texto
  // libre interno. `_key` es un id de cliente estable para el key de React
  // (la lista se reordena/borra, así que no se puede usar el índice).
  const keyRef = useRef(0);
  const nextKey = () => (keyRef.current += 1);
  const [phones, setPhones] = useState([{ prefix: DEFAULT_COUNTRY_PREFIX, number: '', note: '', _key: 0 }]);

  // Prepare country prefix options for react-select
  const countryPrefixOptions = COUNTRY_PREFIXES.map(prefix => ({
    value: prefix.code,
    label: `${prefix.flag} +${prefix.code} ${prefix.country}`,
  }));

  // Get debtor data from API
  const getDataDebtor = async () => {
    try {
      const response = await axios.get(`${baseAPI}/debtors/debtor/${idDebtor}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const { debtorname, debtoradress, debtorcity, debtorzip, debtorphones, debtormail, debtornif } =
        response.data;
      setDebtorData({
        debtorName: debtorname,
        debtorAddress: debtoradress,
        debtorCity: debtorcity,
        debtorZip: debtorzip,
        debtorEmail: debtormail,
        debtornif: debtornif || '',
      });
      // Lista de teléfonos (debtorphones es la fuente de verdad; todos los
      // deudores lo tienen tras la migración).
      const loaded = Array.isArray(debtorphones) && debtorphones.length
        ? debtorphones.map(p => ({ prefix: p.prefix || DEFAULT_COUNTRY_PREFIX, number: p.number || '', note: p.note || '', _key: nextKey() }))
        : [{ prefix: DEFAULT_COUNTRY_PREFIX, number: '', note: '', _key: nextKey() }];
      setPhones(loaded);
    } catch (error) {
      console.log('Error fetching debtor data:', error);
    }
  };

  // Handle input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setDebtorData(prevState => ({ ...prevState, [name]: value }));
  };

  // --- teléfonos (lista dinámica) ---
  const updatePhone = (index, field, value) => {
    setPhones(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };
  const addPhone = () => {
    setPhones(prev => [...prev, { prefix: DEFAULT_COUNTRY_PREFIX, number: '', note: '', _key: nextKey() }]);
  };
  const removePhone = index => {
    setPhones(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };
  const movePhone = (index, dir) => {
    setPhones(prev => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // Submit updated debtor data
  const onSubmit = async e => {
    e.preventDefault();

    try {
      const response = await axios.post(
        `${baseAPI}/debtors/update/debtor/${idDebtor}`,
        {
          debtorname: debtorData.debtorName,
          debtoradress: debtorData.debtorAddress,
          debtorphones: phones
            .filter(p => p.number && p.number.trim())
            .map(p => ({ number: p.number.trim(), prefix: p.prefix || DEFAULT_COUNTRY_PREFIX, note: (p.note || '').trim() })),
          debtormail: debtorData.debtorEmail,
          debtorcity: debtorData.debtorCity,
          debtorzip: debtorData.debtorZip,
          debtornif: debtorData.debtornif,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success('Datos del deudor actualizados correctamente!', {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        toast.error('Error al actualizar los datos del deudor', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error('Error al actualizar los datos del deudor', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Fields definition for dynamic rendering
  const fields = [
    {
      label: 'Nombre',
      name: 'debtorName',
      type: 'text',
      placeholder: 'Nombre',
    },
    {
      label: 'Email',
      name: 'debtorEmail',
      type: 'email',
      placeholder: 'Email',
    },
    {
      label: 'Calle',
      name: 'debtorAddress',
      type: 'text',
      placeholder: 'Calle',
    },
    {
      label: 'Ciudad',
      name: 'debtorCity',
      type: 'text',
      placeholder: 'Ciudad',
    },
    {
      label: 'Código Postal',
      name: 'debtorZip',
      type: 'text',
      placeholder: 'Código Postal',
    },
    {
      label: 'NIF',
      name: 'debtornif',
      type: 'text',
      placeholder: 'NIF',
    },
  ];

  return (
    <div className='w-full'>
      <button
        className='w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        aria-controls='feedback-modal'
        onClick={e => {
          e.stopPropagation();
          setFeedbackModalOpen(true);
          getDataDebtor();
        }}
      >
        <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
          />
        </svg>
        Editar
      </button>

      <ModalBasic
        id='feedback-modal'
        modalOpen={feedbackModalOpen}
        setModalOpen={setFeedbackModalOpen}
        title='Editar datos deudor'
      >
        {/* Modal Content */}
        <section className='px-6 py-5 bg-gray-50 rounded-lg'>
          <form className='flex flex-col flex-wrap space-y-6' onSubmit={onSubmit}>
            <h2 className='text-lg font-semibold text-gray-800 pb-3 border-b border-gray-200'>Datos del deudor</h2>

            {/* Dynamic Field Rendering */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Nombre and Email fields */}
              {fields.slice(0, 2).map(field => (
                <div className='w-full' key={field.name}>
                  <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor={`grid-${field.name}`}>
                    {field.label}
                  </label>
                  <input
                    className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                    id={`grid-${field.name}`}
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={debtorData[field.name]}
                    onChange={handleChange}
                    required={true}
                  />
                </div>
              ))}

              {/* Teléfonos (lista dinámica; el primero es el por defecto de envío) */}
              <div className='w-full md:col-span-2'>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-medium text-gray-700'>Teléfonos</label>
                  <button
                    type='button'
                    onClick={addPhone}
                    className='inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800'
                  >
                    <PlusIcon className='w-4 h-4' />
                    Añadir teléfono
                  </button>
                </div>
                <div className='space-y-3'>
                  {phones.map((phone, index) => (
                    <div key={phone._key} className='rounded-lg border border-gray-200 p-3 bg-white'>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-xs font-medium text-gray-500'>
                          {index === 0 ? 'Principal (por defecto de envío)' : `Teléfono ${index + 1}`}
                        </span>
                        <div className='flex items-center gap-1'>
                          <button
                            type='button'
                            title='Subir'
                            disabled={index === 0}
                            onClick={() => movePhone(index, -1)}
                            className='p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30'
                          >
                            <ChevronUpIcon className='w-4 h-4' />
                          </button>
                          <button
                            type='button'
                            title='Bajar'
                            disabled={index === phones.length - 1}
                            onClick={() => movePhone(index, 1)}
                            className='p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30'
                          >
                            <ChevronDownIcon className='w-4 h-4' />
                          </button>
                          <button
                            type='button'
                            title='Quitar'
                            disabled={phones.length <= 1}
                            onClick={() => removePhone(index)}
                            className='p-1 text-red-500 hover:text-red-700 disabled:opacity-30'
                          >
                            <XMarkIcon className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                      <div className='flex gap-2'>
                        <div className='w-48'>
                          <Select
                            options={countryPrefixOptions}
                            value={countryPrefixOptions.find(o => o.value === phone.prefix)}
                            onChange={opt => updatePhone(index, 'prefix', opt.value)}
                            placeholder='Prefijo'
                            isSearchable={true}
                            className='react-select-container'
                            classNamePrefix='react-select'
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                            styles={{
                              control: base => ({ ...base, borderRadius: '0.5rem', minHeight: '42px' }),
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                        <input
                          className='flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                          type='text'
                          placeholder='Número de teléfono'
                          value={phone.number}
                          onChange={e => updatePhone(index, 'number', e.target.value)}
                          required={index === 0}
                        />
                      </div>
                      <input
                        className='w-full mt-2 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-sm'
                        type='text'
                        placeholder='Nota (uso interno): "el que usa a diario", "no contesta"…'
                        value={phone.note}
                        onChange={e => updatePhone(index, 'note', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Remaining fields: Calle, Ciudad, Código Postal, NIF */}
              {fields.slice(2).map(field => (
                <div className='w-full' key={field.name}>
                  <label className='block text-sm font-medium text-gray-700 mb-2' htmlFor={`grid-${field.name}`}>
                    {field.label}
                  </label>
                  <input
                    className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white'
                    id={`grid-${field.name}`}
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={debtorData[field.name]}
                    onChange={handleChange}
                    required={true}
                  />
                </div>
              ))}
            </div>

            <button
              className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              type='submit'
            >
              Actualizar datos deudor
            </button>
          </form>
        </section>
      </ModalBasic>
    </div>
  );
}

export default UpdateDebtor;
